import React, { useState, useCallback } from 'react';
import Preloader from './Preloader';
import FullScreenMenu from './FullScreenMenu';
import { useAuth } from '@/providers/AuthProvider';

interface PreloaderWrapperProps {
  children: React.ReactNode;
  showPreloader?: boolean;
}

const PreloaderWrapper: React.FC<PreloaderWrapperProps> = ({ 
  children, 
  showPreloader = true 
}) => {
  const { isLoading: authLoading } = useAuth();
  const [isPreloaderComplete, setIsPreloaderComplete] = useState(!showPreloader);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isFullyReady, setIsFullyReady] = useState(!showPreloader);

  const handlePreloaderComplete = useCallback(() => {
    setIsPreloaderComplete(true);
    // Use single rAF for immediate content reveal
    requestAnimationFrame(() => {
      setIsFullyReady(true);
    });
  }, []);

  const handleMenuClose = useCallback(() => {
    setIsMenuOpen(false);
    setIsFullyReady(true);
  }, []);

  return (
    <>
      {!isPreloaderComplete && (
        <Preloader 
          onComplete={handlePreloaderComplete}
          authLoading={authLoading}
        />
      )}
      
      {isMenuOpen && (
        <FullScreenMenu 
          isOpen={isMenuOpen} 
          onClose={handleMenuClose} 
        />
      )}
      
      {/* Always render children, just control visibility for faster perceived load */}
      <div 
        className={`transition-opacity duration-300 ease-out ${
          isPreloaderComplete && isFullyReady ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        style={{
          willChange: isFullyReady ? 'auto' : 'opacity',
          transform: 'translateZ(0)', // GPU layer promotion
        }}
      >
        {children}
      </div>
    </>
  );
};

export default PreloaderWrapper;
